import {ReactElement, useMemo, useState} from "react";
import {Body, Footer, FooterItem, Header} from "@zendeskgarden/react-modals";
import {Button} from "@zendeskgarden/react-buttons";
import {ThemeProvider} from "styled-components";
import {ViziblyTheme} from "../../../../../styles/zendesk-garden/ViziblyZDGTheme";
import {Inline} from "@zendeskgarden/react-loaders";
import {COLORS} from "../../../../../constants/Colors";
import ManualPropertyValidator, {TFormError} from "../../logic/manualPropertyValidator";
import ManualPropertyFormInput from "./ManualPropertyFormInput";

import * as css from "./styles/manualPropertyFormStyles.module.scss";
import ManualPropertyFormDropdown from "./ManualPropertyFormDropdown";
import {Label, Toggle, Field, Checkbox} from "@zendeskgarden/react-forms";
import {PMSPropertyList} from "../PMSPropertyList";
import {ManualProperty, PMSProperty} from "../../logic/types";

export type TManualPropertyRecord = {
    id?: string;
    name: string;
    systemName: string;
    isSystemNameSameAsName?: boolean;
    isDeactivated: boolean;
    reforecastStartMonthIndex: number;
    reforecastYear?: number;
}

export interface IManualPropertyForm {
    isCreate?: boolean,
    isStandard?: boolean,
    manualProperty: TManualPropertyRecord,
    loading: boolean,
    onClose: () => void,
    onChange: (updates: Partial<TManualPropertyRecord>) => void,
    onSubmit: () => void,
    pmsPropertiesData?: PMSProperty[],
    existingProperties?: ManualProperty[],
}

export default function ManualPropertyForm(props: IManualPropertyForm): ReactElement {
    const {
        isCreate = false,
        isStandard = false,
        manualProperty,
        loading,
        onClose,
        onChange,
        onSubmit,
        pmsPropertiesData,
        existingProperties = [],
    } = props;

    const [formErrors, setFormErrors] = useState<TFormError[]>([]);
    const manualPropertyValidator = new ManualPropertyValidator();

    // Months for the dropdown
    const months = [
        { value: 0, name: "January" },
        { value: 1, name: "February" },
        { value: 2, name: "March" },
        { value: 3, name: "April" },
        { value: 4, name: "May" },
        { value: 5, name: "June" },
        { value: 6, name: "July" },
        { value: 7, name: "August" },
        { value: 8, name: "September" },
        { value: 9, name: "October" },
        { value: 10, name: "November" },
        { value: 11, name: "December" }
    ];

    function checkValidOnBlur(field: string): void {
        if (formErrors.find(x => x.field == field)) {
            setFormErrors(manualPropertyValidator.validate(manualProperty, !isCreate && isStandard && !manualProperty.isSystemNameSameAsName));
        }
    }

    function validate(): boolean {
        const errors = manualPropertyValidator.validate(manualProperty, !isCreate && isStandard && !manualProperty.isSystemNameSameAsName);
        setFormErrors(errors);
        return errors.length === 0;
    }

    function submit(): void {
        if (validate()) {
            onSubmit();
        }
    }

    const pmsNameMismatchWarning = useMemo(() => {
        let warning = undefined;
        if(isStandard && (pmsPropertiesData?.length ?? 0) > 0) {
            let lookupName = manualProperty.name;
            if(!manualProperty.isSystemNameSameAsName) {
                lookupName = manualProperty.systemName;
            }
            if(!pmsPropertiesData?.find(p => p.name === lookupName)) {
                warning = "Property name not found in the list of PMS properties. Automated financial and operational data imports are disabled!";
            }
        }

        return warning;
    }, [manualProperty.isSystemNameSameAsName, manualProperty.name, manualProperty.systemName, pmsPropertiesData]);

    const systemNameSameAsNameCheckbox = useMemo(() => {
        return <>
            (<Field>
                (<Checkbox
                    checked={manualProperty.isSystemNameSameAsName}
                    onChange={() => {
                        onChange({isSystemNameSameAsName: !manualProperty.isSystemNameSameAsName});
                    }}
                >
                    <Label isRegular>Same as Display Name</Label>
                </Checkbox>
            </Field>)
        </>;
    }, [manualProperty.isSystemNameSameAsName, onChange]); // keep onChange here as otherwise you'll get stale copy of the function with stale state in it if there is any

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <Header>{!isCreate ? "Edit" : isStandard ? "Onboard" : "Create" } {isStandard ? "PMS" : !isCreate ? "Manual" : ""} Property</Header>
            <Body>
                <form className={css.manualPropertyForm}>
                    <ManualPropertyFormInput
                        label="Display Name"
                        message={!isCreate && isStandard && !manualProperty.isSystemNameSameAsName ? "This is how you see property in Vizibly" : "This is how you see property in Vizibly and your Property Management System"}
                        placeholder="Enter name"
                        required
                        value={manualProperty.name}
                        warning={manualProperty.isSystemNameSameAsName ? pmsNameMismatchWarning : undefined}
                        error={formErrors.find(e => e.field === "name")?.message}
                        onChange={(e) => onChange({name: e.target.value})}
                        onBlur={() => checkValidOnBlur('name')}
                    />

                    {!isCreate && isStandard &&
                        <ManualPropertyFormInput
                            label={<>PMS Import Name&nbsp;{systemNameSameAsNameCheckbox}</>}
                            message="This is how the property is called in your Property Management System"
                            placeholder="Enter name"
                            displayInput={!isCreate && isStandard && !manualProperty.isSystemNameSameAsName}
                            required
                            value={manualProperty.systemName}
                            warning={pmsNameMismatchWarning}
                            error={formErrors.find(e => e.field === "systemName")?.message}
                            onChange={(e) => onChange({systemName: e.target.value})}
                            onBlur={() => checkValidOnBlur('systemName')}
                        />
                    }

                    <ManualPropertyFormDropdown
                        label="Reforecast Start Month"
                        required
                        downshiftProps={{itemToString: (item: typeof months[0] | undefined) => item ? (item.name || item.value.toString()) : ""}}
                        selection={months[manualProperty.reforecastStartMonthIndex]}
                        options={months}
                        error={formErrors.find(e => e.field === "reforecastStartMonthIndex")?.message}
                        onSelect={(item) => onChange({reforecastStartMonthIndex: item.value as number})}
                        onBlur={() => checkValidOnBlur('reforecastStartMonthIndex')}
                    />

                    <Field className={css.fieldMargin}>
                        <Toggle
                            checked={manualProperty.isDeactivated}
                            onChange={(e) => onChange({isDeactivated: e.target.checked})}
                        >
                            <Label>Deactivated</Label>
                        </Toggle>
                    </Field>
                </form>
                {isStandard && pmsPropertiesData && (
                    <div className={css.pmsPropertyList}>
                        <PMSPropertyList
                            pmsPropertiesData={pmsPropertiesData}
                            existingProperties={existingProperties}
                            onPropertySelect={(propertyName) => onChange({systemName: propertyName, name: propertyName})}
                        />
                    </div>
                )}
            </Body>
            <Footer>
                <FooterItem>
                    <Button onClick={onClose} isBasic disabled={loading}>
                        Cancel
                    </Button>
                </FooterItem>
                <FooterItem>
                    <Button isPrimary onClick={submit} disabled={loading}>
                        {loading ? (
                            <Inline size={24} color={COLORS.POSITIVE_COLOR} aria-label="loading" />
                        ) : (
                            <span>Save</span>
                        )}
                    </Button>
                </FooterItem>
            </Footer>
        </ThemeProvider>
    );
}
